import { CSSProperties, FC, MouseEvent, useEffect, useRef, useState } from "react";
import cx from "classnames";

import { CollaboratorRole } from "../../../store/collaborators/types";
import Button, { ButtonVariant } from "./../../../libs/Button";
import { IconTypes } from "./../../../libs/Icon";

import styles from "./styles.module.scss";

interface IProps {
    iconSize?: number;
    icon?: IconTypes;
    text?: string;
    className?: string;
    onRemove?: () => void;
    onBlur?: (relatedTarget: null | FocusEvent["relatedTarget"]) => void;
    scrolled?: boolean;
    clearScrolled?: () => void;
    currentRole: CollaboratorRole;
    onChange: (newValue: CollaboratorRole) => void;
    isFooter?: boolean;
}

const Dropdown: FC<IProps> = ({
    iconSize = 24,
    icon,
    text,
    className,
    onRemove,
    onBlur,
    scrolled,
    clearScrolled,
    currentRole,
    onChange,
    isFooter,
}) => {
    const [open, setOpen] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);
    const [relatedTarget, setRelatedTarget] = useState<null | FocusEvent["relatedTarget"]>(null);
    const [newRole, setNewRole] = useState<CollaboratorRole>(currentRole);

    const dropdown = useRef<HTMLDivElement>(null);

    const onClick = () => {
        if (!open) setOpen(true);
    };

    useEffect(() => {
        function handleClickOutside(event: FocusEvent) {
            if (dropdown.current && !dropdown.current.contains(event.target as HTMLElement)) {
                setOpen(false);
                setRelatedTarget(event.relatedTarget);
            }
        }

        if (open) document.addEventListener("mousedown", handleClickOutside);
        else document.removeEventListener("mousedown", handleClickOutside);

        if (open) document.addEventListener("focusin", handleClickOutside);
        else document.removeEventListener("focusin", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("focusin", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdown, open]);

    useEffect(() => {
        if (scrolled && clearScrolled) {
            setOpen(false);
            if (onBlur) {
                setTimeout(() => onBlur(relatedTarget), 200);
            }
            clearScrolled();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrolled]);

    useEffect(() => {
        if (open && active !== true && dropdown.current) setActive(true);
    }, [open, active, setActive, dropdown]);

    const handleChange = (e: MouseEvent<HTMLButtonElement>, newValue: CollaboratorRole) => {
        e.stopPropagation();
        setNewRole(newValue);
        setOpen(false);
    };

    const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onRemove!();
        setOpen(false);
    };

    return (
        <div className={cx(styles.dropdownWrapper, { [className!]: className })}>
            <Button
                variant={ButtonVariant.PAPER}
                leftIcon={icon}
                leftIconSize={iconSize}
                text={text}
                rightIcon={IconTypes.DOWN}
                rightIconSize={16}
                className={styles.button}
                onClick={onClick}
            />

            {(active || open) && (
                <div
                    ref={dropdown}
                    className={cx(styles.dropdownBox, { [styles.active]: open, [styles.inactive]: !open })}
                    onAnimationEnd={e => {
                        if (!open && active && e.target === dropdown.current) {
                            setActive(false);
                            if (newRole !== currentRole) onChange(newRole);
                        }
                    }}
                    style={
                        {
                            "--dropdownHeight": onRemove ? "177px" : "88px",
                            "--dropdownWidth": onRemove ? "183px" : isFooter ? "96px" : "112px",
                            top: dropdown.current
                                ? Math.ceil(
                                      dropdown.current.parentElement!.getBoundingClientRect().y -
                                          70 -
                                          (onRemove ? 72 : 28),
                                  ) + "px"
                                : "0px",
                        } as CSSProperties
                    }
                >
                    <Button
                        variant={ButtonVariant.PAPER}
                        leftIcon={IconTypes.EDIT}
                        className={cx({ [styles.active]: currentRole === CollaboratorRole.Editor })}
                        leftIconSize={20}
                        text={isFooter ? "edit" : "Editor"}
                        onClick={e => handleChange(e, CollaboratorRole.Editor)}
                    />
                    <Button
                        variant={ButtonVariant.PAPER}
                        leftIcon={IconTypes.EYE}
                        className={cx({ [styles.active]: currentRole === CollaboratorRole.Viewer })}
                        leftIconSize={20}
                        text={isFooter ? "view" : "Viewer"}
                        onClick={e => handleChange(e, CollaboratorRole.Viewer)}
                    />

                    {onRemove && (
                        <>
                            <span className="line" />
                            <Button
                                variant={ButtonVariant.PAPER}
                                text="Transfer Ownership"
                                className={cx({ [styles.active]: currentRole === CollaboratorRole.Owner })}
                                onClick={e => handleChange(e, CollaboratorRole.Owner)}
                            />
                            <Button variant={ButtonVariant.PAPER} text="Remove" onClick={handleRemove} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
